import React from "react";
import HeroSection from "../common/HeroSection";
import {
  FileSearch,
  ClipboardList,
  Lightbulb,
  SlidersHorizontal,
  CircleCheckBig,
} from "lucide-react";
import SapServices from "./SapServices";
import { Link } from "react-router-dom";

export default function SapBtp() {
  return (
    <div>
      <HeroSection
        title="SAP - Business Technology Platform"
        breadcrumb={[
          { name: "Services", url: "/services" },
          { name: "SAP Services", url: "/services/sap-services" },
          {
            name: "SAP - Business Technology Platform",
            url: "/services/sap-services/sap-btp",
          },
        ]}
        background="/assets/images/career.jpg"
      />

      <div className="bg-white">
        <div className="py-16 px-4 mx-auto container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 px-4">
            {/* Sidebar */}
            <SapServices />

            {/* Content */}
            <div className="lg:col-span-2">
              <div className="relative rounded-xl overflow-hidden mb-8">
                <img
                  alt="SAP - Business Technology Platform"
                  src="/assets/images/services/sap-btp.png"
                  className="w-full h-auto object-cover rounded-xl"
                  loading="lazy"
                />
              </div>

              <div className="mt-10">
                <div className="mx-auto">
                  <div className="relative mt-5">
                    <p className="text-gray-700 mb-3 font-bold text-xl">
                      Connect. Extend. Innovate.
                    </p>
                    <p className="text-justify">
                      Accelerate your digital transformation with intelligent,
                      cloud-based integration powered by{" "}
                      <strong>SAP Business Technology Platform (BTP)</strong>.
                      <br />
                      <br />
                      At <strong>TISSA Technology</strong>, we offer specialized{" "}
                      <strong>SAP BTP Integration Services</strong> that help
                      organizations seamlessly connect SAP and non-SAP systems,
                      automate workflows, and create intelligent business
                      applications. Our solutions ensure data flows securely and
                      efficiently across your enterprise — enabling agility,
                      innovation, and operational excellence.
                    </p>

                    <h2 className="text-lg font-semibold mt-6 mb-4 text-blue-700">
                      What We Offer
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          End-to-End System Integration
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Connect SAP S/4HANA, SAP SuccessFactors, SAP Ariba,
                            SAP Concur, and third-party systems (Salesforce,
                            Workday, ServiceNow, etc.).
                          </li>
                          <li>
                            Enable seamless hybrid integrations between
                            on-premise and cloud applications using SAP
                            Integration Suite (formerly CPI).
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          SAP BTP Integration Suite Implementation
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Design, develop, and manage cloud-native integration
                            flows using tools like:
                          </li>
                          <ul className="list-disc pl-5 text-gray-700 font-bold space-y-1">
                            <li>Cloud Integration (CPI)</li>
                            <li>API Management</li>
                            <li>Event Mesh</li>
                            <li>Open Connectors</li>
                          </ul>
                          <li>
                            Real-time data exchange using OData, SOAP, REST, and
                            IDoc adapters.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Pre-Packaged Content & Accelerators
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Leverage SAP’s pre-built integration packages for
                            quick implementation.
                          </li>
                          <li>
                            Customize and extend standard iFlows to meet
                            specific business requirements.
                          </li>
                          <li>
                            Enable faster time-to-value with reduced development
                            effort.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          API Management & Extension
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Securely expose and manage APIs using SAP API
                            Management.
                          </li>
                          <li>
                            Develop and deploy custom APIs, extensions, and
                            microservices using SAP BTP, Kyma, CAP, and Node.js.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Security, Monitoring & Governance
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Implement end-to-end encryption, OAuth2/SAML
                            authentication, and role-based access control.
                          </li>
                          <li>
                            Use Integration Advisor, Cloud ALM, and SAP BTP
                            Cockpit for monitoring, alerting, and lifecycle
                            management.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Event-Driven Architecture
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Build loosely coupled integrations using SAP Event
                            Mesh or Kafka-style messaging.
                          </li>
                          <li>
                            Enable real-time responsiveness across business
                            processes.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Analytics & Intelligent Workflows
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Integrate with SAP Analytics Cloud (SAC) for
                            real-time reporting and dashboards.
                          </li>
                          <li>
                            Automate and orchestrate workflows using SAP
                            Workflow Management and Business Rules Service.
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h2 className="text-lg text-blue-600 font-semibold mb-4">
                        Why TISSA Technology?
                      </h2>
                      <div className="grid grid-cols-1 gap-4 bg-gray-50 border p-6 rounded-md">
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Deep domain experience in ERP, CRM, HR, Supply
                            Chain, and Finance
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Proven expertise in SAP Integration Suite, API
                            Management, BTP Extensions
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Hands-on experience with cloud-to-cloud and hybrid
                            integrations
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Certified SAP BTP consultants and integration
                            architects
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Focus on business agility, reduced integration cost,
                            and scalable architecture
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h2 className="text-lg text-blue-600 font-semibold mb-4">
                        Integration Use Cases We Support
                      </h2>
                      <div className="bg-gray-50 grid grid-cols-1 gap-4 p-6 rounded-xl border-l-4 border-primary">
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>
                            SAP SuccessFactors ↔ SAP S/4HANA ↔ Third-party
                            Payroll
                          </span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>SAP Ariba ↔ Vendor Management System</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>SAP S/4HANA ↔ Salesforce / Dynamics 365</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>SAP ECC ↔ Odoo / Zoho / Custom ERP</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>
                            SAP systems ↔ AI/ML platforms / Data Lakes
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mt-6">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">
                        Unify your enterprise ecosystem with intelligent
                        integration.
                      </h3>
                      <p className="text-gray-700 mb-4">
                        👉{" "}
                        <Link
                          className="text-blue-700 font-bold ms-2 hover:underline"
                          to="/contact-us/"
                        >
                          Partner with TISSA Technology
                        </Link>{" "}
                        for tailored SAP BTP Integration Services that deliver
                        real business impact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* End content */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
